import { useCallback, useEffect, useRef, useState } from "react";
import api from "@/lib/api";

export const REFRESH_MS = 30_000;          // UI refresh while the tab is visible; backend polling cadence is unchanged
export const STALE_AFTER_MS = 90_000;      // no successful update for 3 cycles → stale banner
const EVENT_KINDS = "pos_timing,long_task,loop_delay,api_latency,api_failure,js_error,unhandled_rejection,print";
const EVENT_LIMIT = 500;

// One hook owns all Watchtower data: fleet + per-store browser summary/events, auto-refresh, staleness.
export function useWatchtowerData(includeTest, tenantId, hours) {
  const [fleet, setFleet] = useState(null);
  const [store, setStore] = useState(null);           // { summary, events, fetched_at }
  const [lastOk, setLastOk] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [now, setNow] = useState(Date.now());
  const [hidden, setHidden] = useState(() => typeof document !== "undefined" && document.visibilityState === "hidden");
  const inFlight = useRef(false);
  const queued = useRef(false);                       // a refresh requested mid-flight (e.g. toggle/selection) runs right after
  const latestArgs = useRef({ includeTest, tenantId, hours });
  latestArgs.current = { includeTest, tenantId, hours };

  const refresh = useCallback(async (reason = "manual") => {
    if (inFlight.current) { queued.current = true; return; }   // never two polls in parallel; re-run once the current one ends
    inFlight.current = true;
    const { includeTest: it, tenantId: t, hours: h } = latestArgs.current;
    try {
      const f = await api.get(`/superadmin/watchtower/fleet?include_test=${it}`);
      let s = null;
      const eligible = !!t && (f.data?.systems || []).some((x) => x.tenant_id === t);   // never load a store that is not in the visible fleet list
      if (eligible) {
        const [sum, ev] = await Promise.all([
          api.get(`/superadmin/watchtower/browser/summary?tenant_id=${t}&hours=${h}`),
          api.get(`/superadmin/watchtower/browser/events?tenant_id=${t}&kinds=${EVENT_KINDS}&limit=${EVENT_LIMIT}`),
        ]);
        s = { summary: sum.data, events: ev.data, fetched_at: new Date().toISOString(), event_limit: EVENT_LIMIT, tenant_id: t };
      }
      // keep the view consistent: only commit if the selection did not change while loading
      if (latestArgs.current.tenantId === t && latestArgs.current.includeTest === it) { setFleet(f.data); setStore(s); setLastOk(Date.now()); setError(null); }
    } catch (e) {
      setError(e?.response?.status === 403 ? "Not authorised" : "Watchtower could not refresh");
    } finally { inFlight.current = false; setLoading(false); if (queued.current) { queued.current = false; refresh("queued"); } }
  }, []);

  const prevTenant = useRef(tenantId);
  useEffect(() => { if (prevTenant.current !== tenantId) { prevTenant.current = tenantId; setStore(null); } setLoading(true); refresh("selection"); }, [includeTest, tenantId, hours, refresh]);
  useEffect(() => {
    const tick = () => { setNow(Date.now()); if (document.visibilityState === "visible") refresh("auto"); };
    const id = setInterval(tick, REFRESH_MS);
    const onVis = () => { const h = document.visibilityState !== "visible"; setHidden(h); if (!h) refresh("visible"); };
    document.addEventListener("visibilitychange", onVis);
    const clock = setInterval(() => setNow(Date.now()), 5_000);
    return () => { clearInterval(id); clearInterval(clock); document.removeEventListener("visibilitychange", onVis); };
  }, [refresh]);

  const stale = lastOk ? now - lastOk > STALE_AFTER_MS : false;
  return { fleet, store, lastOk, stale, hidden, error, loading, refresh, busy: inFlight.current };
}
